/**
 * Simple 12‑month projection from funnel inputs + model capacity (not a forecast guarantee).
 * Demand ramps linearly toward a cap derived from stated orders and capacity ceiling.
 */
export type MonthlyProjectionPoint = {
  monthIndex: number;
  /** Calendar month label, e.g. Apr · Dec · Jan ’27 */
  label: string;
  demandUnits: number;
  revenue: number;
  costs: number;
};

/** First month = anchor month (calendar), then consecutive months; year suffix when the year rolls. */
export function projectionMonthLabel(anchor: Date, index: number): string {
  const d = new Date(anchor.getFullYear(), anchor.getMonth() + index, 1);
  const monthShort = d.toLocaleDateString("en-US", { month: "short" });
  if (index === 0) return monthShort;
  const prev = new Date(anchor.getFullYear(), anchor.getMonth() + index - 1, 1);
  if (d.getFullYear() !== prev.getFullYear()) {
    return `${monthShort} ’${String(d.getFullYear()).slice(-2)}`;
  }
  return monthShort;
}

export function buildMonthlyProjections(input: {
  ordersPerMonth: number;
  price: number;
  cost: number;
  capacity: number;
  months?: number;
  /** Defaults to “today” (local). Pass only for tests / storybook. */
  anchorDate?: Date;
}): MonthlyProjectionPoint[] {
  const months = input.months ?? 12;
  const anchor = input.anchorDate ?? new Date();
  const start = Math.max(0, Math.round(input.ordersPerMonth));
  const ceiling = Math.max(start, Math.round(input.capacity * 0.92));

  const out: MonthlyProjectionPoint[] = [];
  for (let m = 0; m < months; m++) {
    const t = months <= 1 ? 1 : m / (months - 1);
    const demandUnits = Math.round(start + (ceiling - start) * t);
    const revenue = demandUnits * input.price;
    const costs = demandUnits * input.cost;
    out.push({
      monthIndex: m,
      label: projectionMonthLabel(anchor, m),
      demandUnits,
      revenue,
      costs,
    });
  }
  return out;
}
